// src/admin/pages/UserDetailPage.jsx
// v206: «Elimina definitivamente…» (greyed for yourself / a superadmin; preview + typed email in
// TypedConfirmDialog; deleted → «OK» returns to the list, anonymised → the page re-reads); an anonymised account
// (…@purged.invalid) shows «Account eliminato» and no action. Failures are worded from err.code, never the error's
// server text.
// v212: the preview and the outcome name the companies the purge leaves with no tenant
// (`companies_without_workspace[]`) with a link — the remedy for CANNOT_REMOVE_LAST_CUSTOMER_ADMIN.
// v215: the «Aziende» card (GET /admin/users/{id}/customers) — the other half of a person's access: a company admin
// reaches every tenant of the company with no tenant membership. Removing someone from a tenant of a company they
// administer says the access stays; the purge's last-admin refusal names the companies (`last_admin_of[]`) with a
// link each. An older backend (404 / no field) shows neither: the card is not drawn, the sentence stays generic.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Shield, ShieldOff, KeyRound, Mail, Trash2, UserPlus } from 'lucide-react';
import AdminTable from '../components/AdminTable.jsx';
import ConfirmDialog from '../components/ConfirmDialog.jsx';
import ResetPasswordModal from './ResetPasswordModal.jsx';
import AssignTenantModal from './AssignTenantModal.jsx';
import OutcomeDialog from '../../components/ui/OutcomeDialog.jsx';
import TypedConfirmDialog from '../purge/TypedConfirmDialog.jsx';
import { PersonPurgeSummary } from '../purge/PurgeSummaries.jsx';
import { usePurgePreview } from '../purge/usePurgePreview.js';
import { firstBlocker, isIncomplete, isPurgedAccount } from '../purge/purgeModel.js';
import { adminErrorText } from '../adminErrorCopy.js';
import {
  companyKeepingAccess, companyPath, lastAdminCompanies, orphanCompanies, orphanLineKeys, userCompanies,
} from '../companies/companyModel.js';
import { LastAdminCompanies, UserCompaniesCard } from '../companies/UserCompanies.jsx';
import { useAuth } from '../../auth/AuthContext.jsx';
import { COLORS, FONT_MONO, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';
import {
  fetchUser, updateUserEmail, activateUser, deactivateUser,
  grantSuperadmin, revokeSuperadmin,
  fetchUserTenants, fetchUserCustomers, removeUserFromTenant, changeUserRole,
  adminListTenants, previewUserPurge, purgeUser,
} from '../adminApi.js';

const cardStyle = {
  background: COLORS.surface, borderRadius: RADII.control,
  border: `1px solid ${COLORS.border}`, padding: '24px',
};

const btnStyle = (color = COLORS.ink) => ({
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  padding: '6px 12px', borderRadius: RADII.controlInner,
  border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
  color, fontSize: '12px', cursor: 'pointer',
  fontFamily: FONT_SANS,
});

export default function UserDetailPage() {
  const { t } = useTranslation('admin');
  const { userId } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [userTenants, setUserTenants] = useState([]);
  // null = unknown (an older backend, a failed read): the «Aziende» card is not drawn.
  const [companies, setCompanies] = useState(null);
  const companiesTicket = useRef(0);
  const [allTenants, setAllTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    adminListTenants().then(setAllTenants).catch(() => setAllTenants([]));
  }, []);

  // Modal states
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [showResetPw, setShowResetPw] = useState(false);
  const [showAssignTenant, setShowAssignTenant] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const { user: me } = useAuth();
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const leaveAfterOutcome = useRef(false);
  const preview = usePurgePreview(purgeOpen, () => previewUserPurge(userId));

  const loadUser = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [userData, tenantData] = await Promise.all([
        fetchUser(userId),
        fetchUserTenants(userId),
      ]);
      setUser(userData);
      setUserTenants(tenantData);
      setNewEmail(userData.email);
    } catch (err) {
      setError(adminErrorText(t, err));
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  // Read on its own, beside loadUser: a failure (an older backend's 404) must not break the page.
  const loadCompanies = useCallback(async () => {
    const ticket = ++companiesTicket.current;
    try {
      const data = await fetchUserCustomers(userId);
      if (ticket === companiesTicket.current) setCompanies(userCompanies(data));
    } catch {
      if (ticket === companiesTicket.current) setCompanies(null);
    }
  }, [userId]);

  useEffect(() => { loadUser(); }, [loadUser]);
  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  async function handleEmailSave() {
    try {
      await updateUserEmail(userId, newEmail);
      setEditingEmail(false);
      loadUser();
    } catch (err) { alert(adminErrorText(t, err)); }
  }

  // The three handlers below are wrapped in ConfirmDialog's onConfirm.
  // The dialog awaits them and surfaces any rejection as an inline error,
  // so they no longer need a local try/catch+alert. ConfirmDialog prints the
  // rejection's message, so each one rethrows OUR sentence (from err.code).
  async function handleToggleActive() {
    try {
      if (user.is_active) await deactivateUser(userId);
      else await activateUser(userId);
    } catch (err) {
      throw new Error(adminErrorText(t, err));
    }
    loadUser();
  }

  async function handleToggleSuperadmin() {
    try {
      if (user.is_superadmin) await revokeSuperadmin(userId);
      else await grantSuperadmin(userId);
    } catch (err) {
      throw new Error(adminErrorText(t, err));
    }
    loadUser();
  }

  async function handleRemoveFromTenant(tenantId) {
    let res;
    try {
      res = await removeUserFromTenant(tenantId, userId);
    } catch (err) {
      throw new Error(adminErrorText(t, err));
    }
    // ⭐ BE 7c5dc05: removing the LAST membership of a never-onboarded account
    // deletes the account itself. There is no user left to show, so leave the page
    // rather than reload a 404.
    if (res?.account_deleted === true) {
      // eslint-disable-next-line no-alert
      alert(t('userDetail.accountDeleted', { email: user?.email }));
      navigate('/admin/users', { replace: true });
      return;
    }
    loadUser();
  }

  async function handleChangeRole(tenantId, newRole) {
    try {
      await changeUserRole(tenantId, userId, newRole);
      loadUser();
    } catch (err) { alert(adminErrorText(t, err)); }
  }

  async function handlePurge(typed) {
    const result = await purgeUser(userId, typed); // a failure: TypedConfirmDialog words it
    setPurgeOpen(false);
    if (isIncomplete(result)) {
      setOutcome({ tone: 'warning', text: t('purge.incomplete') });
      return;
    }
    // Companies this person's purge leaves with no tenant (read before the purge): name them, open the first.
    const orphans = orphanCompanies(result);
    const orphanText = orphanLineKeys(orphans).map((l) => ` ${t(l.key, l.params)}`).join('');
    const action = orphans.length > 0
      ? { label: t('purge.orphan.open'), onClick: () => navigate(companyPath(orphans[0].customer_id)) }
      : undefined;
    const item = Array.isArray(result?.users) ? result.users[0] : null;
    if (item?.outcome === 'anonymised') {
      await loadUser(); // the page now shows «Account eliminato» (D8)
      loadCompanies();
      setOutcome({
        tone: 'success',
        text: `${t('purge.person.outcomeAnonymised', { email: item.anonymous_email || '' })}${orphanText}`,
        ...(action ? { action } : {}),
      });
      return;
    }
    leaveAfterOutcome.current = true; // the account is gone: «OK» returns to the list (the action wins: it runs after)
    setOutcome({ tone: 'success', text: `${t('purge.person.outcomeDeleted')}${orphanText}`, ...(action ? { action } : {}) });
  }

  function closeOutcome() {
    setOutcome(null);
    if (leaveAfterOutcome.current) {
      leaveAfterOutcome.current = false;
      navigate('/admin/users', { replace: true });
    }
  }

  if (loading) return <div style={{ padding: '40px', color: COLORS.textSecondary, fontFamily: FONT_SANS }}>{t('shared.loading')}</div>;
  if (error) return <div style={{ padding: '40px', color: SEMANTIC.errore.fg, fontFamily: FONT_SANS }}>{error}</div>;
  if (!user) return null;

  const purged = isPurgedAccount(user);
  const isSelf = !!me?.user_id && me.user_id === user.user_id;
  // D3 — greyed upfront where the reason is known locally; self first, like the backend's refusal order.
  let purgeDisabledReason = null;
  if (isSelf) purgeDisabledReason = t('purge.person.disabledSelf');
  else if (user.is_superadmin) purgeDisabledReason = t('purge.person.disabledSuperadmin');
  const purgeBlocker = firstBlocker(preview.preview);
  let purgeBlockedText = null;
  if (preview.error) purgeBlockedText = adminErrorText(t, preview.error, { purge: true });
  else if (purgeBlocker === 'CANNOT_REMOVE_LAST_CUSTOMER_ADMIN' && lastAdminCompanies(preview.preview).length > 0) {
    purgeBlockedText = <LastAdminCompanies companies={lastAdminCompanies(preview.preview)} />;
  } else if (purgeBlocker) purgeBlockedText = adminErrorText(t, { code: purgeBlocker });

  // userTenants from fetchUserTenants() returns the JWT-shape {tenant_id, ...}.
  // allTenants from adminListTenants() returns the new {id, ...} shape.
  const assignedTenantIds = new Set(userTenants.map(t => t.tenant_id));
  const availableTenants = allTenants.filter(t => !assignedTenantIds.has(t.id));
  const tenantCompanyId = new Map(allTenants.map(tn => [tn.id, tn.customer_id]));

  function removeMessage(row) {
    const message = t('userDetail.removeMessage', { email: user.email, tenant: row.display_name });
    const keeping = companyKeepingAccess(tenantCompanyId.get(row.tenant_id), companies);
    return keeping
      ? `${message} ${t('userDetail.removeKeepsAccess', { company: keeping.legal_name || keeping.customer_id })}`
      : message;
  }

  const nameColumn = { key: 'display_name', label: t('shared.tenant') };
  const tenantColumns = purged ? [nameColumn] : [
    nameColumn,
    {
      key: 'role', label: t('shared.role'), width: '140px',
      render: (row) => (
        <select
          value={row.role}
          onChange={e => handleChangeRole(row.tenant_id, e.target.value)}
          style={{ padding: '4px 8px', borderRadius: RADII.controlInner, border: `1px solid ${COLORS.borderStrong}`, fontSize: '12px', fontFamily: FONT_SANS }}
        >
          {['viewer', 'editor', 'admin'].map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      ),
    },
    {
      key: 'actions', label: '', width: '80px',
      render: (row) => (
        <button
          onClick={() => setConfirmAction({
            title: t('userDetail.removeTitle'),
            message: removeMessage(row),
            onConfirm: () => handleRemoveFromTenant(row.tenant_id),
            variant: 'danger',
          })}
          style={btnStyle(SEMANTIC.errore.fg)}
        >
          <Trash2 size={13} /> {t('shared.remove')}
        </button>
      ),
    },
  ];

  return (
    <div style={{ padding: '32px 40px', fontFamily: FONT_SANS, color: COLORS.ink, maxWidth: '900px' }}>
      <button onClick={() => navigate('/admin/users')} style={{ ...btnStyle(), marginBottom: '20px', background: 'transparent', border: 'none' }}>
        <ArrowLeft size={15} /> {t('shared.back')}
      </button>

      <div style={{ ...cardStyle, marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            {editingEmail ? (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
                  style={{ padding: '6px 12px', borderRadius: RADII.controlInner, border: `1px solid ${COLORS.borderStrong}`, fontSize: '16px', fontWeight: 600, fontFamily: FONT_SANS }}
                />
                <button onClick={handleEmailSave} style={btnStyle(SEMANTIC.verde.fg)}>{t('common:actions.save')}</button>
                <button onClick={() => { setEditingEmail(false); setNewEmail(user.email); }} style={btnStyle()}>{t('common:actions.cancel')}</button>
              </div>
            ) : (
              <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>
                {user.email}
                {!purged && (
                  <button onClick={() => setEditingEmail(true)} style={{ ...btnStyle(), marginLeft: '8px', padding: '3px 8px' }}>
                    <Mail size={13} /> {t('userDetail.edit')}
                  </button>
                )}
              </h2>
            )}
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px', fontSize: '12px', color: COLORS.textSecondary, flexWrap: 'wrap' }}>
              <span>{t('userDetail.id')} <code style={{ fontFamily: FONT_MONO, fontSize: '11px' }}>{user.user_id}</code></span>
              {/* Name / locale are read-only here — there is no admin write path for another user's profile. */}
              {(user.first_name || user.last_name) && (
                <span>{t('userDetail.name')} {[user.first_name, user.last_name].filter(Boolean).join(' ')}</span>
              )}
              {user.locale && <span>{t('userDetail.locale')} {user.locale}</span>}
              {user.created_at && <span>{t('userDetail.created')} {new Date(user.created_at).toLocaleDateString('it-IT')}</span>}
              {/* last_login_at shares created_at's non-ISO "YYYY-MM-DD HH:MM:SS.ffffff+00:00" format; parse it the same way. */}
              {user.last_login_at && <span>{t('userDetail.lastLogin')} {new Date(user.last_login_at).toLocaleString('it-IT')}</span>}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            {purged ? (
              <span style={{
                padding: '4px 10px', borderRadius: RADII.controlInner, fontSize: '12px', fontWeight: 600,
                background: COLORS.borderStrong, color: COLORS.surface,
              }}>
                {t('purge.person.deletedMarker')}
              </span>
            ) : (
              <span style={{
                padding: '4px 10px', borderRadius: RADII.controlInner, fontSize: '12px', fontWeight: 600,
                background: user.is_active ? SEMANTIC.verde.bg : SEMANTIC.errore.bg,
                color: user.is_active ? SEMANTIC.verde.fg : SEMANTIC.errore.fg,
              }}>
                {user.is_active ? t('shared.active') : t('shared.inactive')}
              </span>
            )}
            {user.is_superadmin && (
              <span style={{ padding: '4px 10px', borderRadius: RADII.controlInner, fontSize: '12px', fontWeight: 600, background: COLORS.accentSoft, color: COLORS.accent }}>
                {t('userDetail.superadmin')}
              </span>
            )}
          </div>
        </div>

        {!purged && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={() => setShowResetPw(true)} style={btnStyle()}>
              <KeyRound size={13} /> {t('userDetail.resetPassword')}
            </button>
            <button
              onClick={() => setConfirmAction({
                title: user.is_active ? t('userDetail.deactivateTitle') : t('userDetail.activateTitle'),
                message: user.is_active
                  ? t('userDetail.deactivateMessage', { email: user.email })
                  : t('userDetail.activateMessage', { email: user.email }),
                onConfirm: handleToggleActive,
                variant: user.is_active ? 'danger' : 'default',
              })}
              style={btnStyle(user.is_active ? SEMANTIC.errore.fg : SEMANTIC.verde.fg)}
            >
              {user.is_active ? t('userDetail.deactivate') : t('userDetail.activate')}
            </button>
            <button
              onClick={() => setConfirmAction({
                title: user.is_superadmin ? t('userDetail.revokeSaTitle') : t('userDetail.promoteSaTitle'),
                message: user.is_superadmin
                  ? t('userDetail.revokeSaMessage', { email: user.email })
                  : t('userDetail.promoteSaMessage', { email: user.email }),
                onConfirm: handleToggleSuperadmin,
                variant: user.is_superadmin ? 'danger' : 'default',
              })}
              style={btnStyle(user.is_superadmin ? SEMANTIC.errore.fg : COLORS.accent)}
            >
              {user.is_superadmin ? <ShieldOff size={13} /> : <Shield size={13} />}
              {user.is_superadmin ? t('userDetail.revokeSa') : t('userDetail.promoteSa')}
            </button>
            <button
              type="button"
              onClick={() => setPurgeOpen(true)}
              disabled={Boolean(purgeDisabledReason)}
              title={purgeDisabledReason || undefined}
              style={{ ...btnStyle(SEMANTIC.errore.fg), opacity: purgeDisabledReason ? 0.5 : 1, cursor: purgeDisabledReason ? 'not-allowed' : 'pointer' }}
            >
              <Trash2 size={13} /> {t('purge.person.purge')}
            </button>
          </div>
        )}
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, margin: 0 }}>{t('userDetail.tenantsHeading')}</h3>
          {!purged && (
            <button onClick={() => setShowAssignTenant(true)} style={btnStyle(COLORS.accent)}>
              <UserPlus size={13} /> {t('userDetail.assignTenant')}
            </button>
          )}
        </div>
        <AdminTable
          columns={tenantColumns}
          rows={userTenants}
          emptyMessage={t('userDetail.noTenants')}
        />
      </div>

      <UserCompaniesCard companies={companies} style={{ ...cardStyle, marginTop: '24px' }} />

      <ResetPasswordModal open={showResetPw} onClose={() => setShowResetPw(false)} userId={userId} />
      <AssignTenantModal
        open={showAssignTenant}
        onClose={() => setShowAssignTenant(false)}
        userEmail={user.email}
        availableTenants={availableTenants}
        onAssigned={loadUser}
      />
      {confirmAction && (
        <ConfirmDialog
          open={true}
          onClose={() => setConfirmAction(null)}
          onConfirm={confirmAction.onConfirm}
          title={confirmAction.title}
          message={confirmAction.message}
          variant={confirmAction.variant}
        />
      )}
      <TypedConfirmDialog
        open={purgeOpen}
        title={t('purge.person.title', { email: user.email })}
        expected={user.email}
        caseInsensitive
        inputLabel={t('purge.person.typeLabel')}
        confirmLabel={t('purge.person.confirm')}
        loading={preview.loading}
        onRetry={preview.reload}
        blockedText={purgeBlockedText}
        onConfirm={handlePurge}
        onClose={() => setPurgeOpen(false)}
      >
        {preview.preview && <PersonPurgeSummary preview={preview.preview} knownTenants={userTenants} />}
      </TypedConfirmDialog>
      <OutcomeDialog outcome={outcome} onClose={closeOutcome} />
    </div>
  );
}
